/**
 * NarratorIntroCard — the personal "Hi John, it's Margaret…" greeting
 * that plays before each narrator's first memory in the session.
 *
 * Architecture (robust against slow networks / codec gaps):
 *   • The PHOTO is ALWAYS rendered as the base layer (no black flash).
 *   • A standalone AUDIO element drives playback timing — small, reliable,
 *     and matches the same TTS narration baked into the HeyGen MP4.
 *   • The HeyGen VIDEO is overlaid MUTED on top of the photo and
 *     crossfades in only when it's ready to play. If it never loads,
 *     the photo stays — the user still gets the audio greeting.
 *   • `onEnded` on the audio element (NOT the video) triggers transition
 *     to the next memory — so timing is deterministic even if video lags.
 */
import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ForgetMeNotIcon } from "./ForgetMeNotIcon";
import { useAudioStabilizer } from "../lib/useAudioStabilizer";
import { VolumeControl } from "./VolumeControl";

const POST_END_HOLD_MS = 400;       // tiny pause after the audio ends before we cut to the story
const NO_MEDIA_HOLD_MS = 2400;      // fallback if there's no audio at all (e.g. legacy data)
const AUTOPLAY_BLOCK_HOLD_MS = 3500; // if autoplay is blocked, hold the card briefly then move on
const PHOTO_READY_SAFETY_MS = 6000; // hard ceiling for photo decode wait — never block the demo longer

export function NarratorIntroCard({
    audioUrl,
    videoUrl,
    photoUrl,
    displayName,
    relationship,
    onComplete,
    onSkip,
    theme = "dark",   // "dark" (adults) | "light" (kids)
    profileFirstName,
}) {
    const audioRef = useRef(null);
    const videoRef = useRef(null);
    const [videoReady, setVideoReady] = useState(false);
    const [videoErrored, setVideoErrored] = useState(false);

    // Stabilise the parent's onComplete / onSkip callbacks behind refs so
    // the lifecycle effect below DOESN'T re-run every time the parent
    // (JohnsDemo) re-renders. JohnsDemo re-renders many times per second
    // from audio progress + paused state ticks, and each render hands us
    // a fresh `() => setIntroNarrator(null)` arrow. Without the ref, the
    // useEffect saw a new `onComplete` reference every tick → cleanup
    // fired `a.pause()` → effect re-ran → `a.play()` again → the audio
    // never made it to the `ended` event and the card stayed stuck
    // forever. Glenn's iter-20 bug report: "Stephens talking head photo
    // loads stays static with his narration - session does not progress
    // from there".
    const onCompleteRef = useRef(onComplete);
    useEffect(() => { onCompleteRef.current = onComplete; }, [onComplete]);
    const onSkipRef = useRef(onSkip);
    useEffect(() => { onSkipRef.current = onSkip; }, [onSkip]);

    // photoReady gates the entire visible card AND audio playback so the
    // viewer never sees an empty portrait frame with Margaret's voice
    // playing over it. We fall back to mounting after a 6 s safety
    // timeout so a stuck image-fetch never strands the demo.
    const [photoReady, setPhotoReady] = useState(!photoUrl);
    useEffect(() => {
        if (!photoUrl) { setPhotoReady(true); return; }
        // Re-evaluate when the URL changes (new narrator).
        setPhotoReady(false);
        // Use a detached Image so we can listen for decode independent of
        // React's render timing. Same URL → same browser cache key → the
        // visible <img> below will pick it up instantly once we render it.
        const im = new Image();
        let safety = null;
        const done = () => {
            if (safety) clearTimeout(safety);
            setPhotoReady(true);
        };
        im.onload = done;
        im.onerror = done; // fail-open — don't strand the demo
        im.decoding = "async";
        im.fetchPriority = "high";
        im.src = photoUrl;
        // Already cached? `complete` will be true synchronously after src.
        if (im.complete && im.naturalWidth > 0) done();
        else safety = setTimeout(done, PHOTO_READY_SAFETY_MS);
        return () => { if (safety) clearTimeout(safety); im.onload = null; im.onerror = null; };
    }, [photoUrl]);

    const showVideo = Boolean(videoUrl) && !videoErrored;

    // WebAudio dynamics chain on the standalone <audio> so the caregiver
    // Quiet/Comfortable/Loud presets still apply during intros.
    const { volume, preset, setVolume, setPreset } = useAudioStabilizer(audioRef, audioUrl);

    // ---- Lifecycle: audio drives timing, video tags along muted ----
    useEffect(() => {
        const a = audioRef.current;
        let dismissTimer = null;
        let cleanup = false;

        // Wait until the portrait photo is ready before kicking off audio.
        // This is the fix for "empty frame with Margaret's voice playing
        // over it" — the card mounts immediately but stays visually muted
        // until the photo's decoded (or the safety timer trips after 1.5s).
        if (!photoReady) return;

        // No audio at all (legacy data with neither audio nor video) — hold static.
        if (!a || !audioUrl) {
            dismissTimer = setTimeout(() => { if (!cleanup) onCompleteRef.current?.(); }, NO_MEDIA_HOLD_MS);
            return () => { cleanup = true; if (dismissTimer) clearTimeout(dismissTimer); };
        }

        const onEnded = () => {
            if (cleanup) return;
            dismissTimer = setTimeout(() => { if (!cleanup) onCompleteRef.current?.(); }, POST_END_HOLD_MS);
        };
        a.addEventListener("ended", onEnded);

        // Defensive ceiling — once we know the audio's duration, schedule
        // a guaranteed dismiss at `duration + 2 s` so even if `ended`
        // never fires (codec quirk, mid-stream pause/play interruption,
        // browser bug, etc.) the card NEVER strands the session. Glenn's
        // iter-20 stuck-on-Stephen bug was the trigger — keeping this in
        // even though the ref-stabilisation is the real fix.
        let safetyDismiss = null;
        const onLoadedMeta = () => {
            const d = a.duration;
            if (d && isFinite(d) && d > 0) {
                if (safetyDismiss) clearTimeout(safetyDismiss);
                safetyDismiss = setTimeout(() => {
                    if (!cleanup) onCompleteRef.current?.();
                }, d * 1000 + 2000);
            }
        };
        a.addEventListener("loadedmetadata", onLoadedMeta);
        if (a.readyState >= 1) onLoadedMeta(); // already loaded? schedule now

        const tryPlay = async () => {
            try {
                await a.play();
                // Sync the video to the same timeline — muted overlay on top.
                const v = videoRef.current;
                if (v && videoUrl && !videoErrored) {
                    try { v.currentTime = 0; v.play().catch(() => {/* swallow */}); } catch (_) {/**/}
                }
            } catch (_e) {
                if (cleanup) return;
                // Autoplay blocked or audio error — hold the static card briefly.
                dismissTimer = setTimeout(() => { if (!cleanup) onCompleteRef.current?.(); }, AUTOPLAY_BLOCK_HOLD_MS);
            }
        };
        tryPlay();

        return () => {
            cleanup = true;
            if (dismissTimer) clearTimeout(dismissTimer);
            if (safetyDismiss) clearTimeout(safetyDismiss);
            a.removeEventListener("ended", onEnded);
            a.removeEventListener("loadedmetadata", onLoadedMeta);
            try { a.pause(); } catch (_) {/**/}
            try { videoRef.current?.pause(); } catch (_) {/**/}
        };
    // `onComplete` intentionally excluded — its identity changes on every
    // parent re-render and inclusion caused the audio to stutter / never
    // end (Glenn's iter-20 report). We invoke through `onCompleteRef.current`
    // so the latest closure fires without re-triggering the effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [audioUrl, videoUrl, videoErrored, photoReady]);

    // ---- Styling ----
    const isDark = theme !== "light";
    const wrapColours = isDark
        ? "bg-gradient-to-br from-[#1a1c25] via-[#22253A] to-[#1a1c25] text-white"
        : "bg-gradient-to-br from-[#FFF1F4] via-[#FFE3EA] to-[#FFF1F4] text-[#3A2A30]";
    const eyebrowColour = isDark ? "text-[#A8B5E0]" : "text-[#C97B8E]";
    const cardRing = isDark
        ? "ring-1 ring-white/10 bg-white/[0.04]"
        : "ring-1 ring-[#C97B8E]/15 bg-white/60";

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: photoReady ? 1 : 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            className={`absolute inset-0 z-50 flex flex-col items-center justify-center px-6 ${wrapColours}`}
            data-testid="narrator-intro-card"
        >
            {/* Eyebrow */}
            <motion.p
                initial={{ y: 12, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className={`text-[10px] sm:text-xs uppercase tracking-[0.32em] mb-4 ${eyebrowColour}`}
                data-testid="narrator-intro-eyebrow"
            >
                A message for {profileFirstName || "you"}
            </motion.p>

            {/* Portrait card — photo always visible, muted video crossfades on top when ready */}
            <motion.div
                initial={{ scale: 0.96, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                className={`relative w-[min(78vw,420px)] aspect-[9/16] rounded-3xl overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.45)] ${cardRing}`}
            >
                {/* Portrait layers — photo as base, HeyGen talking-head video
                    on top. Both reference the same photoUrl (the video's
                    `poster` attribute matches the IMG src exactly), so when
                    the video starts playing the transition feels like the
                    same face simply coming to life — no abrupt photo-to-
                    video snap.

                    Why two elements rather than one with poster only?
                    Some browsers (notably Chromium in NETWORK_LOADING state)
                    suppress the poster while actively fetching the video,
                    which left an empty dark portrait for the duration of the
                    download. The IMG base layer guarantees the photo is on
                    screen instantly (it's pre-warmed by JohnsDemo's narrator
                    prefetch effect), and the video crossfades in once
                    `onCanPlay` fires. */}
                {photoUrl ? (
                    <img
                        src={photoUrl}
                        alt={displayName}
                        className="absolute inset-0 w-full h-full object-cover"
                        loading="eager"
                        decoding="async"
                        data-testid="narrator-intro-photo"
                    />
                ) : (
                    <div className={`absolute inset-0 flex items-center justify-center ${isDark ? "bg-white/[0.04]" : "bg-white/40"}`}>
                        <ForgetMeNotIcon size={88} variant={isDark ? "mono" : "colour"} />
                    </div>
                )}

                {showVideo && (
                    <video
                        ref={videoRef}
                        src={videoUrl}
                        poster={photoUrl || undefined}
                        muted
                        playsInline
                        autoPlay
                        preload="auto"
                        onCanPlay={() => {
                            setVideoReady(true);
                            // Re-attempt play in case the initial autoplay was
                            // throttled by the browser BEFORE canplay was ready.
                            try { videoRef.current?.play().catch(() => {}); } catch (_) { /**/ }
                        }}
                        onLoadedData={() => {
                            // Earlier than canplay — fires as soon as the first
                            // frame is decoded. We use it as a second chance to
                            // kick playback so the avatar doesn't stay frozen
                            // on the poster while audio rolls underneath.
                            try { videoRef.current?.play().catch(() => {}); } catch (_) { /**/ }
                        }}
                        onError={() => setVideoErrored(true)}
                        // Always rendered at opacity 1: the browser shows the
                        // `poster` (same photo as the IMG base layer behind)
                        // while bytes buffer, then transitions seamlessly to
                        // playback. No videoReady-gated opacity transition —
                        // that mechanism left the video invisible whenever
                        // `onCanPlay` failed to fire (Glenn's real-browser
                        // report: "no Margaret talking head video").
                        className="absolute inset-0 w-full h-full object-cover"
                        data-testid="narrator-intro-video"
                    >
                        <track kind="captions" />
                    </video>
                )}

                {/* Soft top/bottom vignettes for legibility */}
                <div className={`pointer-events-none absolute inset-x-0 top-0 h-16 ${isDark ? "bg-gradient-to-b from-black/40 to-transparent" : "bg-gradient-to-b from-white/40 to-transparent"}`} />
                <div className={`pointer-events-none absolute inset-x-0 bottom-0 h-28 ${isDark ? "bg-gradient-to-t from-black/65 to-transparent" : "bg-gradient-to-t from-white/65 to-transparent"}`} />

                {/* Caption: NAME + RELATIONSHIP — pinned to the bottom of the card */}
                <div className="absolute inset-x-0 bottom-4 px-4 text-center">
                    <p
                        className="font-serif italic text-3xl sm:text-4xl leading-none text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]"
                        data-testid="narrator-intro-name"
                    >
                        {displayName}
                    </p>
                    <p className="mt-1.5 text-xs sm:text-sm italic text-white/90 drop-shadow-[0_1px_4px_rgba(0,0,0,0.65)]">
                        {relationship}
                    </p>
                </div>
            </motion.div>

            {/* Pulsing dot — gentle "audio is playing" cue */}
            <motion.div
                animate={{ scale: [1, 1.18, 1], opacity: [0.4, 0.85, 0.4] }}
                transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                className={`mt-7 h-2 w-2 rounded-full ${isDark ? "bg-[#A8B5E0]" : "bg-[#C97B8E]"}`}
                aria-hidden
            />

            {/* Caregiver controls + skip */}
            <div className="absolute bottom-6 inset-x-0 flex flex-col items-center gap-3 px-4">
                <VolumeControl volume={volume} preset={preset} setVolume={setVolume} setPreset={setPreset} theme={isDark ? "dark" : "light"} />
                <button
                    onClick={() => onSkipRef.current?.()}
                    className={`text-xs uppercase tracking-[0.24em] px-4 py-1.5 rounded-full transition-colors ${
                        isDark
                            ? "text-white/65 hover:text-white hover:bg-white/10"
                            : "text-[#3A2A30]/55 hover:text-[#3A2A30] hover:bg-white/40"
                    }`}
                    data-testid="narrator-intro-skip"
                    aria-label="Skip this introduction"
                >
                    Skip intro
                </button>
            </div>

            {/* Hidden audio element — the timing source of truth */}
            <audio
                ref={audioRef}
                src={audioUrl}
                preload="auto"
                data-testid="narrator-intro-audio"
            />
        </motion.div>
    );
}
